import type { AxiosResponse } from 'axios';
import type { SagaIterator } from 'redux-saga';
import { call, put, takeEvery } from 'redux-saga/effects';

import track, { AnalyticsEventName } from '@app/utils/analytics';

import {
    createSnapshotCall,
    deleteSnapshotCall,
    deleteSnapshotsCall,
    getAllSnapshots,
    restoreSnapshotCall,
    setAutomaticBulkActionsCall,
    setAutomaticUpdateCall,
    setAutomaticUploadCall,
    updateSnapshotCall,
} from './api';
import { projectSnapshotsActions } from './reducers';
import type { GetSnapshots } from './types';

const {
    createSnapshot,
    deleteSnapshot,
    deleteSnapshots,
    getSnapshots,
    restoreSnapshot,
    setAutomaticUpdate,
    setAutomaticUpload,
    setAutomaticBulkActions,
    setSnapshotsInfo,
    setSnapshotsLoading,
    setSnapshotUpdate,
    updateAutomaticUpdate,
    updateAutomaticUpload,
    updateAutomaticBulkActions,
    updateSnapshot,
} = projectSnapshotsActions;

function* getSnapshotsWatcher(action: ReturnType<typeof getSnapshots>): SagaIterator {
    const { paginationData, projectId } = action.payload;

    yield put(setSnapshotsLoading('getSnapshots'));

    let { data }: AxiosResponse<GetSnapshots> = yield call(getAllSnapshots, projectId, paginationData);

    if (!data.content.length && data.offset) {
        const prevPaginationData = {
            ...paginationData,
            page: paginationData.page - 1,
        };

        const record = yield call(getAllSnapshots, projectId, prevPaginationData);

        data = record.data;
    }

    yield put(setSnapshotsInfo(data));
    yield put(setSnapshotsLoading(''));
}

function* setAutomaticUploadWatcher(action: ReturnType<typeof setAutomaticUpload>) {
    const { enabled, projectId } = action.payload;
    yield put(setSnapshotsLoading('putAutoUpload'));
    yield setAutomaticUploadCall(projectId, enabled);
    yield put(setSnapshotsLoading(''));
    yield put(updateAutomaticUpload(enabled));
}

function* setAutomaticUpdateWatcher(action: ReturnType<typeof setAutomaticUpdate>) {
    const { enabled, projectId } = action.payload;

    yield put(setSnapshotsLoading('putAutoUpload'));
    yield setAutomaticUpdateCall(projectId, enabled);
    yield put(setSnapshotsLoading(''));
    yield put(updateAutomaticUpdate(enabled));
}

function* setAutomaticBulkActionsWatcher(action: ReturnType<typeof setAutomaticBulkActions>) {
    const { enabled, projectId } = action.payload;

    yield put(setSnapshotsLoading('putAutoBulkActions'));
    yield setAutomaticBulkActionsCall(projectId, enabled);
    yield put(setSnapshotsLoading(''));
    yield put(updateAutomaticBulkActions(enabled));
}

function* updateSnapshotWatcher(action: ReturnType<typeof updateSnapshot>) {
    const { projectId, description, snapshotCreatedAt, index } = action.payload;

    yield put(setSnapshotsLoading('updateSnapshot'));
    updateSnapshotCall(projectId, snapshotCreatedAt, description);
    yield put(setSnapshotsLoading(''));

    yield put(setSnapshotUpdate({ description, index }));
}

function* restoreSnapshotWatcher(action: ReturnType<typeof restoreSnapshot>) {
    const { snapshotCreatedAt, projectId } = action.payload;

    yield put(setSnapshotsLoading('restoreSnapshot'));
    yield restoreSnapshotCall(projectId, snapshotCreatedAt);
    yield put(setSnapshotsLoading(''));
}

function* deleteSnapshotWatcher(action: ReturnType<typeof deleteSnapshot>) {
    const { snapshotCreatedAt, projectId, paginationData } = action.payload;

    yield put(setSnapshotsLoading('deleteSnapshot'));
    yield deleteSnapshotCall(projectId, snapshotCreatedAt);
    yield put(setSnapshotsLoading(''));
    yield put(getSnapshots(projectId, paginationData));
}

function* deleteSnapshotsWatcher(action: ReturnType<typeof deleteSnapshots>) {
    const { projectId, snapshotsCreatedAt, paginationData } = action.payload;

    yield put(setSnapshotsLoading('deleteSnapshots'));
    yield deleteSnapshotsCall(projectId, snapshotsCreatedAt);
    yield put(setSnapshotsLoading(''));
    yield put(getSnapshots(projectId, paginationData));
}

function* createSnapshotWatcher(action: ReturnType<typeof createSnapshot>) {
    const { projectId, paginationData } = action.payload;

    yield put(setSnapshotsLoading('createSnapshot'));
    yield createSnapshotCall(projectId).then(() => {
        track(AnalyticsEventName.SNAPSHOT_CREATED);
    });
    yield put(setSnapshotsLoading(''));
    yield put(getSnapshots(projectId, paginationData));
}

export default function* initSnapshotsSagas() {
    yield takeEvery(getSnapshots.match, getSnapshotsWatcher);
    yield takeEvery(setAutomaticUpload.match, setAutomaticUploadWatcher);
    yield takeEvery(setAutomaticUpdate.match, setAutomaticUpdateWatcher);
    yield takeEvery(setAutomaticBulkActions.match, setAutomaticBulkActionsWatcher);
    yield takeEvery(updateSnapshot.match, updateSnapshotWatcher);
    yield takeEvery(restoreSnapshot.match, restoreSnapshotWatcher);
    yield takeEvery(deleteSnapshot.match, deleteSnapshotWatcher);
    yield takeEvery(deleteSnapshots.match, deleteSnapshotsWatcher);
    yield takeEvery(createSnapshot.match, createSnapshotWatcher);
}
